import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, Edit, Package, Plus, ChevronLeft, ChevronRight, Calendar, DollarSign, Wallet, FileText, CheckCircle2, IndianRupee, Loader2, SlidersHorizontal, X, History } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";
import OrderRedFlagsDialog, { OrderFlagBadge } from "@/components/OrderRedFlagsDialog";
import { SellerBalance } from "@/components/SellerBalance";

// Appointment edit history is recorded on the order's status_history as these entries
// (see routes/appointments.py + routes/order.py): each set / reschedule / edit appends one.
const APPT_HISTORY_STATUSES = ["APPOINTMENT_SET", "APPOINTMENT_SCHEDULED", "APPOINTMENT_UPDATED"];
const APPT_ACTION_LABEL = {
  APPOINTMENT_SET: "Appointment set",
  APPOINTMENT_SCHEDULED: "Scheduled with partner",
  APPOINTMENT_UPDATED: "Appointment updated",
};
const getApptHistory = (order) =>
  (order?.status_history || [])
    .filter((h) => h && APPT_HISTORY_STATUSES.includes(h.status))
    .slice()
    .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
import AppointmentWarning from "@/components/AppointmentWarning";
import { TIME_RANGES, validateAppointmentWithinPoExpiry, isDelhiveryAppointmentBooked } from "@/utils/appointmentUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

const STATUS_COLORS = {
  CREATED: "bg-blue-100 text-blue-800",
  MANIFESTED: "bg-purple-100 text-purple-800",
  PICKED_UP: "bg-yellow-100 text-yellow-800",
  LEFT_ORIGIN: "bg-orange-100 text-orange-800",
  REACH_DESTINATION: "bg-cyan-100 text-cyan-800",
  UNDEL_REATTEMPT: "bg-amber-100 text-amber-800",
  PART_DEL: "bg-lime-100 text-lime-800",
  OFD: "bg-teal-100 text-teal-800",
  DELIVERED: "bg-green-100 text-green-800",
};

const GRN_STATUS_COLORS = {
  Pending: "bg-yellow-100 text-yellow-800",
  Partial: "bg-blue-100 text-blue-800",
  Complete: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

const GRN_STATUSES = ["Pending", "Partial", "Complete", "Rejected"];
const PAYMENT_METHODS = ["Cash", "Card", "UPI", "Net Banking", "Cheque", "Others"];

const ITEMS_PER_PAGE = 50;

// Full order-status set for the sidebar Status filter (mirrors AdminOrders + CANCELLED,
// which is set at runtime by the cancel flow and has its own Appointments tab).
const ORDER_STATUSES = [
  "CREATED", "MANIFESTED", "PICKED_UP", "LEFT_ORIGIN", "REACH_DESTINATION",
  "UNDEL_REATTEMPT", "PART_DEL", "OFD", "DELIVERED", "RETURNED_INTRANSIT",
  "RECEIVED_AT_RETURN_CENTER", "RETURN_OFD", "RETURN_DELIVERED", "NOT_PICKED", "LOST", "CANCELLED",
];

// Advanced-filter option lists — identical to the B2B Orders page so both surfaces
// filter the orders collection the same way. payment_mode / insurance_option are exact
// enums; freight_mode / pickup_type are the known set.
const PAYMENT_MODES = [{ v: 'Prepaid', l: 'Prepaid' }, { v: 'COD', l: 'COD' }];
const INSURANCE_OPTIONS = [{ v: 'owner_risk', l: 'Owner Risk' }, { v: 'carrier_insurance', l: 'Carrier Insurance' }];
const PICKUP_TYPES = [{ v: 'fm_pickup', l: 'First-Mile Pickup' }, { v: 'self_drop', l: 'Self Drop' }];
const FREIGHT_MODES = [{ v: 'BTC', l: 'BTC' }, { v: 'TBB', l: 'TBB' }, { v: 'TBP', l: 'TBP' }];
const YES_NO = [{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }];
const EWAY_OPTS = [{ v: 'present', l: 'Present' }, { v: 'absent', l: 'Absent' }];

const FilterSelect = ({ label, value, onChange, placeholder, options }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const DateRangeField = ({ label, s, e, onS, onE }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <Input type="date" value={s} onChange={(ev) => onS(ev.target.value)} />
      <Input type="date" value={e} onChange={(ev) => onE(ev.target.value)} />
    </div>
  </div>
);

const NumRangeField = ({ label, lo, hi, onLo, onHi }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <Input type="number" min="0" placeholder="Min" value={lo} onChange={(ev) => onLo(ev.target.value)} />
      <Input type="number" min="0" placeholder="Max" value={hi} onChange={(ev) => onHi(ev.target.value)} />
    </div>
  </div>
);

// The 8 Appointments tabs (must match backend APPOINTMENT_TABS keys).
const APPOINTMENT_TABS = [
  { key: 'breached', label: 'Breached' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'pending', label: 'Pending' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'returned', label: 'Returned' },
  { key: 'lost', label: 'Lost' },
  { key: 'cancelled', label: 'Cancelled' },
];
// Active (to-be-delivered) tabs get the schedule/edit-appointment actions; the
// terminal tabs get the GRN/payment columns.
const ACTIVE_TAB_KEYS = ['breached', 'today', 'tomorrow', 'pending'];

const AdminAppointments = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useLocalStorageState('admin-appointments:tab', 'breached');
  const [tabCounts, setTabCounts] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  
  // Summary metrics
  const [summary, setSummary] = useState({
    total_pending: 0,
    today_pending: 0,
    appointment_breached: 0,
    total_grn_pending: 0,
    pending_collection_amount: 0,
    total_history: 0
  });
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useLocalStorageState('admin-appointments:search', "");
  const [selectedMarketplace, setSelectedMarketplace] = useLocalStorageState('admin-appointments:marketplace', "");
  const [selectedSeller, setSelectedSeller] = useLocalStorageState('admin-appointments:seller', "");
  const [selectedStatus, setSelectedStatus] = useLocalStorageState('admin-appointments:status', "");
  const [startDate, setStartDate] = useLocalStorageState('admin-appointments:startDate', "");
  const [endDate, setEndDate] = useLocalStorageState('admin-appointments:endDate', "");
  const [marketplaces, setMarketplaces] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [shippingPartners, setShippingPartners] = useState([]);

  // Advanced filters (persisted as one object) + the right slide-out panel.
  const [adv, setAdv] = useLocalStorageState('admin-appointments:adv', {});
  const [filterOpen, setFilterOpen] = useState(false);
  const setAdvField = (key, value) => setAdv((prev) => ({ ...(prev || {}), [key]: value }));
  const advKey = JSON.stringify(adv || {});

  // Dialog states
  const [showGRNDialog, setShowGRNDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPaymentsListDialog, setShowPaymentsListDialog] = useState(false);
  const [schedulingOrderId, setSchedulingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // GRN form states
  const [grnStatus, setGrnStatus] = useState("Pending");
  const [dispatchQty, setDispatchQty] = useState(0);
  const [acceptedQty, setAcceptedQty] = useState(0);
  const [rejectedQty, setRejectedQty] = useState(0);
  const [grnRemarks, setGrnRemarks] = useState("");
  
  // Payment form states
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [transactionRef, setTransactionRef] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  
  // Payments list
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsRemaining, setPaymentsRemaining] = useState(0);
  
  // Edit appointment states
  // Appointment edit-history modal
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyOrder, setHistoryOrder] = useState(null);
  // Per-LRN Ops Red Flags panel
  const [flagsByOrder, setFlagsByOrder] = useState({});   // order.id -> active flags[]
  const [availableActions, setAvailableActions] = useState([]);
  const [showFlagsDialog, setShowFlagsDialog] = useState(false);
  const [flagsOrder, setFlagsOrder] = useState(null);
  const reqIdRef = useRef(0);   // guards out-of-order list/flag responses (see fetchOrders)
  const [showEditAppointmentDialog, setShowEditAppointmentDialog] = useState(false);
  const [editingAppointmentOrder, setEditingAppointmentOrder] = useState(null);
  const [editAppointmentDate, setEditAppointmentDate] = useState("");
  const [editAppointmentTime, setEditAppointmentTime] = useState("");
  const [editApptDelhiveryId, setEditApptDelhiveryId] = useState("");   // Delhivery appointment ID input
  // Manual (EdgeLMS-only) update mode: the Edit dialog is reused with manualMode=true,
  // which shows the confirmation-method + comment and calls the LMS-only endpoint (no carrier).
  const [manualMode, setManualMode] = useState(false);
  const [manualMethod, setManualMethod] = useState("");
  const [manualMethodOther, setManualMethodOther] = useState("");
  const [manualComment, setManualComment] = useState("");

  // Confirmation methods for an EdgeLMS-only (manual) appointment update.
  const MANUAL_METHODS = [
    { value: "green_channel", label: "Updated Via Green Channel" },
    { value: "partner_email", label: "Updated Via Partner confirmed on Email" },
    { value: "partner_whatsapp", label: "Updated Via Partner confirmed on Whatsapp" },
    { value: "partner_sms", label: "Updated Via Partner confirmed on SMS" },
    { value: "other", label: "Other" },
  ];
  const [editingAppointmentId, setEditingAppointmentId] = useState(null);  // busy-flag (order id being saved)

  // Schedule appointment dialog states
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleOrder, setScheduleOrder] = useState(null);
  const [poNumbersInput, setPoNumbersInput] = useState("");
  const [appointmentIdInput, setAppointmentIdInput] = useState("");
  const [poExpiryInput, setPoExpiryInput] = useState("");
  const [poNumbersError, setPoNumbersError] = useState("");

  // Check if appointment can be edited based on conditions
  const canEditAppointment = (order) => {
    if (!order.appointment_date) return false;
    // Low-balance sellers can't (re)book — the backend enforces this too.
    if (order.seller_wallet?.is_low) return false;

    const now = new Date();
    const currentHour = now.getHours();
    
    // Parse appointment date (format: DD-MMM-YYYY or YYYY-MM-DD)
    let appointmentDate;
    try {
      if (order.appointment_date.includes('-')) {
        // Try parsing different formats
        const parts = order.appointment_date.split('-');
        if (parts[0].length === 4) {
          // YYYY-MM-DD format
          appointmentDate = new Date(order.appointment_date);
        } else if (parts[1].length === 3) {
          // DD-MMM-YYYY format (e.g., 25-Jan-2026)
          appointmentDate = new Date(order.appointment_date);
        } else {
          // DD-MM-YYYY format
          appointmentDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
      } else {
        appointmentDate = new Date(order.appointment_date);
      }
    } catch {
      return false;
    }
    
    if (isNaN(appointmentDate.getTime())) return false;
    
    // Get today's date (start of day)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get tomorrow's date (start of day)
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Get appointment date (start of day)
    const apptDateOnly = new Date(appointmentDate);
    apptDateOnly.setHours(0, 0, 0, 0);
    
    // Rule 1: Same day appointment - Cannot be edited
    if (apptDateOnly.getTime() === today.getTime()) {
      return false;
    }

    // Rule 2: Tomorrow's appointment - Can only be edited before 6 PM today
    if (apptDateOnly.getTime() === tomorrow.getTime()) {
      return currentHour < 18; // Before 6 PM (18:00)
    }

    // Rule 3: Future (day-after-tomorrow onwards) OR PAST/breached appointments are editable —
    // a breached (missed) appointment can be re-booked to a new future date. The new date is
    // still validated on save (must be tomorrow..+15 days and within PO expiry). Only same-day
    // and tomorrow-after-6PM stay locked.
    return true;
  };
  
  // Get reason why appointment cannot be edited
  const getEditDisabledReason = (order) => {
    if (!order.appointment_date) return "No appointment scheduled";
    if (order.seller_wallet?.is_low) return "Seller wallet is at/below the minimum — recharge required before editing the appointment";

    const now = new Date();
    const currentHour = now.getHours();
    
    let appointmentDate;
    try {
      if (order.appointment_date.includes('-')) {
        const parts = order.appointment_date.split('-');
        if (parts[0].length === 4) {
          appointmentDate = new Date(order.appointment_date);
        } else if (parts[1].length === 3) {
          appointmentDate = new Date(order.appointment_date);
        } else {
          appointmentDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
      } else {
        appointmentDate = new Date(order.appointment_date);
      }
    } catch {
      return "Invalid appointment date";
    }
    
    if (isNaN(appointmentDate.getTime())) return "Invalid appointment date";
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const apptDateOnly = new Date(appointmentDate);
    apptDateOnly.setHours(0, 0, 0, 0);
    
    if (apptDateOnly.getTime() === today.getTime()) {
      return "Same day appointments cannot be edited";
    }
    
    if (apptDateOnly.getTime() === tomorrow.getTime() && currentHour >= 18) {
      return "Tomorrow's appointment can only be edited before 6 PM";
    }
    
    return "";
  };

  useEffect(() => {
    fetchSummary();
    fetchMarketplaces();
    fetchSellers();
    fetchShippingPartners();
    // Ops red-flag action list (same options as the Ops Red Flags page). Best-effort.
    axios.get(`${API}/red-flags/actions/available`)
      .then((r) => setAvailableActions(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Any tab/filter change resets to page 1 (a page change alone does not).
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, selectedMarketplace, selectedSeller, selectedStatus, startDate, endDate, advKey]);

  useEffect(() => {
    fetchOrders();
  }, [activeTab, currentPage, searchQuery, selectedMarketplace, selectedSeller, selectedStatus, startDate, endDate, advKey]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/appointments/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  // Build the shared query params (base filters + advanced sidebar filters). tab/page
  // are only added for the list call; tab-counts spans all tabs.
  const buildAppointmentParams = (includeTabAndPage) => {
    const p = {};
    if (searchQuery && searchQuery.trim()) p.search = searchQuery.trim();
    if (selectedMarketplace && selectedMarketplace !== "all") p.marketplace_id = selectedMarketplace;
    if (selectedSeller && selectedSeller !== "all") p.seller_id = selectedSeller;
    if (selectedStatus && selectedStatus !== "all") p.status = selectedStatus;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    // Advanced filters (sidebar): only non-empty, non-"all" values are sent.
    Object.entries(adv || {}).forEach(([k, v]) => {
      if (v !== "" && v != null && v !== "all") p[k] = v;
    });
    if (includeTabAndPage) {
      p.tab = activeTab;
      p.page = currentPage;
      p.limit = ITEMS_PER_PAGE;
    }
    return p;
  };

  const fetchTabCounts = async () => {
    try {
      const response = await axios.get(`${API}/appointments/tab-counts`, { params: buildAppointmentParams(false) });
      setTabCounts(response.data);
    } catch (error) {
      console.error("Failed to fetch tab counts:", error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    // Monotonic request id: a fast tab/page/filter switch can leave an older list (and its
    // flag) request in flight; only the most recent one may write state, so a late stale
    // response can't clobber the newer view.
    const myReqId = ++reqIdRef.current;
    try {
      const response = await axios.get(`${API}/appointments/list`, { params: buildAppointmentParams(true) });
      if (reqIdRef.current !== myReqId) return;  // superseded by a newer fetch
      setOrders(response.data.orders);
      setTotalPages(response.data.pages);
      setTotalOrders(response.data.total);
      // Keep the tab badges in sync with any list refresh (incl. after mutations).
      fetchTabCounts();
      // Load active Ops red flags for this page's orders (one indexed call for all row badges).
      fetchOrderFlags(response.data.orders, myReqId);
    } catch (error) {
      if (reqIdRef.current === myReqId) toast.error("Failed to fetch appointments");
      console.error(error);
    } finally {
      if (reqIdRef.current === myReqId) setLoading(false);
    }
  };

  // Active Ops red flags for a page of orders, keyed by order.id (entity_id). Non-fatal:
  // a failure just means the badges won't show — the appointments list is unaffected.
  // reqId ties this to its originating fetchOrders so a stale response can't overwrite a newer
  // view; on failure we clear (rather than leave stale badges from the previous view).
  const fetchOrderFlags = async (list, reqId) => {
    const isLatest = () => reqId === undefined || reqIdRef.current === reqId;
    const ids = (list || []).map((o) => o.id).filter(Boolean);
    if (!ids.length) { if (isLatest()) setFlagsByOrder({}); return; }
    try {
      const res = await axios.post(`${API}/red-flags/by-orders`, { order_ids: ids });
      if (isLatest()) setFlagsByOrder(res.data.flags_by_order || {});
    } catch (error) {
      if (isLatest()) setFlagsByOrder({});
      console.error("Failed to fetch red flags for orders", error);
    }
  };

  // Refresh a single order's flags after an action (so the badge count + modal update in place).
  const refreshOrderFlags = async (orderId) => {
    if (!orderId) return;
    try {
      const res = await axios.post(`${API}/red-flags/by-orders`, { order_ids: [orderId] });
      const next = (res.data.flags_by_order || {})[orderId] || [];
      setFlagsByOrder((prev) => ({ ...prev, [orderId]: next }));
    } catch (error) {
      console.error("Failed to refresh red flags", error);
    }
  };

  const fetchMarketplaces = async () => {
    try {
      const response = await axios.get(`${API}/b2b-marketplace/`);
      setMarketplaces(response.data);
    } catch (error) {
      console.error("Failed to fetch marketplaces");
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      setSellers(response.data);
    } catch (error) {
      console.error("Failed to fetch sellers");
    }
  };

  const fetchShippingPartners = async () => {
    try {
      // Credential-stripped, view-gated feed (already is_active-filtered). The plain
      // /b2b-shipping-partner/ endpoint is manage-gated and returns live courier creds.
      const response = await axios.get(`${API}/b2b-shipping-partner/active`);
      setShippingPartners(response.data);
    } catch (error) {
      console.error("Failed to fetch shipping partners");
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedMarketplace("");
    setSelectedSeller("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setAdv({});
    setCurrentPage(1);
  };

  const handleScheduleAppointment = async (order) => {
    // Check if integrated shipping partner
    const shippingPartner = order.shipping_partner_name || '';
    if (!shippingPartner.toLowerCase().includes('delhivery')) {
      toast.error(`Shipping partner "${shippingPartner}" is not integrated for automatic scheduling`);
      return;
    }

    // Check if has LRN
    if (!order.lrn) {
      toast.error("Order does not have an LRN assigned. Please assign LRN first.");
      return;
    }

    // Check if has appointment
    if (!order.appointment_date && !order.appointment_datetime) {
      toast.error("Order does not have an appointment set");
      return;
    }

    // Open the schedule dialog
    setScheduleOrder(order);
    // Pre-fill PO numbers from reference_number if available
    setPoNumbersInput(order.reference_number || "");
    // Pre-fill appointment_id if exists
    setAppointmentIdInput(order.delhivery_appointment_id || order.appointment_id || "");
    // Pre-fill PO Expiry Date (Delhivery requires it) — YYYY-MM-DD for the date input.
    setPoExpiryInput((order.po_expiry_date || "").slice(0, 10));
    setPoNumbersError("");
    setShowScheduleDialog(true);
  };

  const validatePoNumbers = (input) => {
    if (!input || input.trim() === "") {
      return "At least one PO number is required. Enter 'NotApplicable' if not applicable.";
    }
    const poList = input.split(",").map(p => p.trim()).filter(p => p);
    if (poList.length === 0) {
      return "At least one PO number is required.";
    }
    if (poList.length > 5) {
      return "Maximum 5 PO numbers allowed.";
    }
    return "";
  };

  const handleConfirmSchedule = async () => {
    if (!scheduleOrder) return;

    // Validate PO numbers
    const error = validatePoNumbers(poNumbersInput);
    if (error) {
      setPoNumbersError(error);
      return;
    }

    // PO Expiry Date is required by Delhivery — must be present (pre-filled or entered).
    if (!poExpiryInput) {
      toast.error("PO Expiry Date is required to book with Delhivery. Please enter it.");
      return;
    }

    // Appointment ID is required only when this order's B2B marketplace opts in.
    const apptIdRequired = !!marketplaces.find((m) => m.id === scheduleOrder?.b2b_marketplace_id)?.appointment_id_required;
    if (apptIdRequired && (!appointmentIdInput || !appointmentIdInput.trim())) {
      toast.error("Appointment ID is required to book an appointment for this marketplace.");
      return;
    }

    // Convert comma-separated string to array
    const poNumbersList = poNumbersInput.split(",").map(p => p.trim()).filter(p => p);

    const appointmentInfo = scheduleOrder.appointment_date 
      ? `${scheduleOrder.appointment_date} (${scheduleOrder.appointment_time_range || 'N/A'})`
      : new Date(scheduleOrder.appointment_datetime).toLocaleString();
    
    const confirmed = window.confirm(
      `This will add/update the appointment in EdgeLMS AND book it with ${scheduleOrder.shipping_partner_name} on ${appointmentInfo}.\n\nPO Numbers: ${poNumbersList.join(", ")}\n${appointmentIdInput ? `Appointment ID: ${appointmentIdInput}` : ""}\n\nDo you want to proceed?`
    );

    if (!confirmed) return;

    setSchedulingOrderId(scheduleOrder.id);
    setShowScheduleDialog(false);
    
    try {
      const payload = {
        po_numbers: poNumbersList,
        po_expiry_date: poExpiryInput,
        appointment_id: appointmentIdInput.trim(),
      };

      const response = await axios.post(
        `${API}/appointments/orders/${scheduleOrder.id}/schedule`,
        payload
      );
      
      toast.success(response.data.message || "Appointment scheduled successfully!");
      if (response.data.delhivery_appointment_id) {
        toast.info(`Delhivery Appointment ID: ${response.data.delhivery_appointment_id}`);
      }
      fetchOrders(); // Refresh the list
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to schedule appointment";
      toast.error(errorMsg);
    } finally {
      setSchedulingOrderId(null);
      setScheduleOrder(null);
      setPoNumbersInput("");
      setAppointmentIdInput("");
      setPoExpiryInput("");
    }
  };

  const handleOpenEditAppointment = (order) => {
    setManualMode(false);
    setEditingAppointmentOrder(order);
    // Parse existing appointment date for the form
    setEditAppointmentDate(order.appointment_date || "");
    setEditAppointmentTime(order.appointment_time_range || "");
    setEditApptDelhiveryId(order.delhivery_appointment_id || order.appointment_id || "");
    setShowEditAppointmentDialog(true);
  };

  // Manual (EdgeLMS-only) appointment update — reuses the Edit dialog with manualMode on.
  const handleOpenManualUpdate = (order) => {
    setManualMode(true);
    setEditingAppointmentOrder(order);
    setEditAppointmentDate(order.appointment_date || "");
    setEditAppointmentTime(order.appointment_time_range || "");
    setEditApptDelhiveryId(order.delhivery_appointment_id || order.appointment_id || "");
    setManualMethod("");
    setManualMethodOther("");
    setManualComment("");
    setShowEditAppointmentDialog(true);
  };

  const handleManualUpdate = async () => {
    if (!editingAppointmentOrder) return;
    if (!editAppointmentDate) { toast.error("Please select an appointment date"); return; }
    if (!editAppointmentTime) { toast.error("Please select a time slot"); return; }
    if (!manualMethod) { toast.error("Select how the appointment was confirmed with the partner"); return; }
    if (manualMethod === "other" && !manualMethodOther.trim()) {
      toast.error("Please describe the 'Other' confirmation method"); return;
    }
    const poErr = validateAppointmentWithinPoExpiry(editingAppointmentOrder.reference_numbers, editAppointmentDate, editingAppointmentOrder.po_expiry_date);
    if (poErr) { toast.error(poErr); return; }

    setEditingAppointmentId(editingAppointmentOrder.id);
    try {
      await axios.post(`${API}/appointments/orders/${editingAppointmentOrder.id}/appointment/manual`, {
        appointment_date: editAppointmentDate,
        appointment_time_range: editAppointmentTime,
        appointment_id: editApptDelhiveryId.trim(),
        update_method: manualMethod,
        update_method_other: manualMethodOther.trim(),
        comment: manualComment.trim(),
      });
      toast.success("Appointment updated in EdgeLMS only (partner not called).");
      setShowEditAppointmentDialog(false);
      setEditingAppointmentOrder(null);
      setManualMode(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setEditingAppointmentId(null);
    }
  };

  const handleEditAppointment = async () => {
    if (!editingAppointmentOrder) return;
    
    if (!editAppointmentDate) {
      toast.error("Please select an appointment date");
      return;
    }
    
    if (!editAppointmentTime) {
      toast.error("Please select a time slot");
      return;
    }

    // Appointment must not be later than the order's earliest PO expiry date.
    const poErr = validateAppointmentWithinPoExpiry(editingAppointmentOrder.reference_numbers, editAppointmentDate, editingAppointmentOrder.po_expiry_date);
    if (poErr) {
      toast.error(poErr);
      return;
    }

    const editApptIdRequired = !!marketplaces.find((m) => m.id === editingAppointmentOrder?.b2b_marketplace_id)?.appointment_id_required;
    if (editApptIdRequired && !editApptDelhiveryId.trim()) {
      toast.error("Appointment ID is required to book an appointment for this marketplace.");
      return;
    }

    // This books in EdgeLMS AND the shipping partner — confirm the carrier call.
    const partner = editingAppointmentOrder.shipping_partner_name || "the shipping partner";
    if (!window.confirm(
      `This will add/update the appointment in EdgeLMS AND book it with ${partner} for ${editAppointmentDate} (${editAppointmentTime}).\n\nProceed?`
    )) return;

    setEditingAppointmentId(editingAppointmentOrder.id);

    try {
      // Update the appointment. The backend auto-books/reschedules with Delhivery
      // (when the order has an LRN + Delhivery partner) and returns the result.
      const resp = await axios.put(`${API}/appointments/orders/${editingAppointmentOrder.id}/appointment`, {
        appointment_date: editAppointmentDate,
        appointment_time_range: editAppointmentTime,
        appointment_id: editApptDelhiveryId.trim()
      });
      const booking = resp.data?.delhivery_booking;
      if (booking?.success) {
        toast.success("Appointment updated and booked with Delhivery!");
      } else if (booking && !booking.skipped && booking.error) {
        toast.warning("Appointment updated, but Delhivery booking failed. You can retry from Schedule.");
      } else {
        toast.success("Appointment updated successfully!");
      }
      
      setShowEditAppointmentDialog(false);
      setEditingAppointmentOrder(null);
      setEditAppointmentDate("");
      setEditAppointmentTime("");
      setEditApptDelhiveryId("");
      fetchOrders();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to update appointment";
      toast.error(errorMsg);
    } finally {
      setEditingAppointmentId(null);
    }
  };

  // Use the shared, Delhivery-aligned slot list (see appointmentUtils.TIME_RANGES)
  // so admin, seller, and order-creation flows all offer identical slots.
  const timeSlots = TIME_RANGES;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    // Filters persist across tabs (same UX as the B2B Orders page); use "Clear all" to reset.
  };

  // Active (to-be-delivered) tabs show schedule/edit-appointment actions; terminal
  // tabs (delivered/returned/lost/cancelled) show the GRN/payment columns.
  const isActiveTab = ACTIVE_TAB_KEYS.includes(activeTab);

  const handleOpenGRNDialog = async (order) => {
    setSelectedOrder(order);
    
    // Fetch existing GRN if any
    try {
      const response = await axios.get(`${API}/appointments/grn/${order.id}`);
      if (response.data.grn) {
        const grn = response.data.grn;
        setGrnStatus(grn.grn_status || "Pending");
        setDispatchQty(grn.total_dispatch_quantity || 0);
        setAcceptedQty(grn.total_accepted_quantity || 0);
        setRejectedQty(grn.total_rejected_quantity || 0);
        setGrnRemarks(grn.remarks || "");
      } else {
        // Reset form for new GRN
        setGrnStatus("Pending");
        setDispatchQty(0);
        setAcceptedQty(0);
        setRejectedQty(0);
        setGrnRemarks("");
      }
    } catch (error) {
      console.error("Failed to fetch GRN:", error);
    }
    
    setShowGRNDialog(true);
  };

  const handleSaveGRN = async () => {
    try {
      const grnData = {
        order_id: selectedOrder.id,
        grn_status: grnStatus,
        total_dispatch_quantity: parseInt(dispatchQty),
        total_accepted_quantity: parseInt(acceptedQty),
        total_rejected_quantity: parseInt(rejectedQty),
        remarks: grnRemarks
      };
      
      await axios.post(`${API}/appointments/grn`, grnData);
      toast.success("GRN updated successfully");
      setShowGRNDialog(false);
      fetchOrders();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update GRN");
    }
  };

  const handleOpenPaymentDialog = (order) => {
    setSelectedOrder(order);
    // Set default payment date to today
    const today = new Date().toISOString().split('T')[0];
    setPaymentDate(today);
    setPaymentMethod("Cash");
    setPaymentAmount("");
    setTransactionRef("");
    setPaymentRemarks("");
    setShowPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    try {
      const paymentData = {
        order_id: selectedOrder.id,
        payment_method: paymentMethod,
        amount_collected: parseFloat(paymentAmount),
        payment_date: new Date(paymentDate).toISOString(),
        transaction_reference: transactionRef || null,
        remarks: paymentRemarks || null
      };
      
      const response = await axios.post(`${API}/appointments/payment`, paymentData);
      toast.success(`Payment recorded: ₹${paymentAmount}. Remaining: ₹${response.data.remaining}`);
      setShowPaymentDialog(false);
      fetchOrders();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleViewPayments = async (order) => {
    setSelectedOrder(order);
    try {
      const response = await axios.get(`${API}/appointments/payments/${order.id}`);
      setPaymentsList(response.data.payments);
      setPaymentsTotal(response.data.total_collected);
      setPaymentsRemaining(response.data.remaining);
      setShowPaymentsListDialog(true);
    } catch (error) {
      toast.error("Failed to fetch payment history");
    }
  };

  // Active-filter chips (base + advanced) for the summary strip and the Filters count badge.
  const ADV_LABELS = {
    shipping_partner_id: 'Partner', payment_mode: 'Payment', freight_mode: 'Freight',
    pickup_type: 'Pickup', insurance_option: 'Insurance', lrn_assigned: 'LRN', eway_bill: 'E-way',
    boxes_min: 'Boxes ≥', boxes_max: 'Boxes ≤', amount_min: 'Amount ≥', amount_max: 'Amount ≤',
    appt_start: 'Appt ≥', appt_end: 'Appt ≤', manifested_start: 'Manifested ≥',
    manifested_end: 'Manifested ≤', delivered_start: 'Delivered ≥', delivered_end: 'Delivered ≤',
  };
  const activeChips = [
    (selectedMarketplace && selectedMarketplace !== 'all') && { k: 'mp', label: `Marketplace: ${marketplaces.find((m) => m.id === selectedMarketplace)?.name || selectedMarketplace}`, clear: () => setSelectedMarketplace('') },
    (selectedSeller && selectedSeller !== 'all') && { k: 'seller', label: `Seller: ${sellers.find((s) => s.seller_id === selectedSeller)?.brand_name || sellers.find((s) => s.seller_id === selectedSeller)?.company_registered_name || selectedSeller}`, clear: () => setSelectedSeller('') },
    (selectedStatus && selectedStatus !== 'all') && { k: 'status', label: `Status: ${selectedStatus.replace(/_/g, ' ')}`, clear: () => setSelectedStatus('') },
    startDate && { k: 'sd', label: `Created ≥ ${startDate}`, clear: () => setStartDate('') },
    endDate && { k: 'ed', label: `Created ≤ ${endDate}`, clear: () => setEndDate('') },
    ...Object.entries(adv || {})
      .filter(([, v]) => v !== '' && v != null && v !== 'all')
      .map(([k, v]) => ({
        k,
        label: `${ADV_LABELS[k] || k}: ${k === 'shipping_partner_id' ? (shippingPartners.find((p) => p.id === v)?.name || v) : v}`,
        clear: () => setAdvField(k, ''),
      })),
  ].filter(Boolean);
  const activeFilterCount = activeChips.length;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Appointments</h1>
          <p className="text-slate-600 mt-1">Manage delivery appointments and collections</p>
        </div>

        <div className="mb-6">
          <AppointmentWarning />
        </div>

        {/* Summary Cards — Breached / Today / Pending totals now live in the tabs below,
            so only the non-tab metrics (GRN + Collection) are surfaced here. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">GRN Pending</p>
                  <p className="text-xl font-bold text-slate-800">{summary.total_grn_pending}</p>
                </div>
                <FileText className="w-6 h-6 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Collection Pending</p>
                  <p className="text-xl font-bold text-slate-800">₹{summary.pending_collection_amount.toLocaleString()}</p>
                </div>
                <IndianRupee className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-x-1 gap-y-0 mb-4 border-b border-slate-200">
          {APPOINTMENT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`px-3 py-2.5 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === t.key
                  ? 'border-b-2 border-teal-600 text-teal-600'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {t.label} ({tabCounts[t.key] ?? 0})
            </button>
          ))}
        </div>

        {/* Search + Filters trigger + active-filter chips */}
        <Card className="mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <SearchInput
                  onSearch={setSearchQuery}
                  placeholder="Search by LRN, Reference No., Invoice..."
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setFilterOpen(true)} className="whitespace-nowrap">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-teal-600 text-white text-xs font-semibold h-5 min-w-[1.25rem] px-1.5">{activeFilterCount}</span>
                  )}
                </Button>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" onClick={handleClearFilters} className="whitespace-nowrap text-slate-600">Clear all</Button>
                )}
              </div>
            </div>
            {activeChips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeChips.map((chip) => (
                  <span key={chip.k} className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1">
                    {chip.label}
                    <button onClick={chip.clear} className="text-slate-400 hover:text-slate-700" aria-label="Remove filter">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right slide-out filter panel — every appointment/order filter grouped */}
        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetContent side="right" className="w-[92vw] sm:max-w-md overflow-y-auto flex flex-col">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="flex-1 space-y-6 py-4">
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Basics</p>
                <FilterSelect label="Marketplace" value={selectedMarketplace} onChange={setSelectedMarketplace} placeholder="All Marketplaces"
                  options={[{ v: 'all', l: 'All Marketplaces' }, ...marketplaces.map((m) => ({ v: m.id, l: m.name }))]} />
                <FilterSelect label="Seller" value={selectedSeller} onChange={setSelectedSeller} placeholder="All Sellers"
                  options={[{ v: 'all', l: 'All Sellers' }, ...sellers.map((s) => ({ v: s.seller_id, l: s.brand_name || s.company_registered_name }))]} />
                <FilterSelect label="Status" value={selectedStatus} onChange={setSelectedStatus} placeholder="All Statuses"
                  options={[{ v: 'all', l: 'All Statuses' }, ...ORDER_STATUSES.map((s) => ({ v: s, l: s.replace(/_/g, ' ') }))]} />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Dates</p>
                <DateRangeField label="Appointment" s={adv.appt_start || ''} e={adv.appt_end || ''} onS={(v) => setAdvField('appt_start', v)} onE={(v) => setAdvField('appt_end', v)} />
                <DateRangeField label="Created" s={startDate} e={endDate} onS={setStartDate} onE={setEndDate} />
                <DateRangeField label="Manifested" s={adv.manifested_start || ''} e={adv.manifested_end || ''} onS={(v) => setAdvField('manifested_start', v)} onE={(v) => setAdvField('manifested_end', v)} />
                <DateRangeField label="Delivered" s={adv.delivered_start || ''} e={adv.delivered_end || ''} onS={(v) => setAdvField('delivered_start', v)} onE={(v) => setAdvField('delivered_end', v)} />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Shipment</p>
                <FilterSelect label="Payment Mode" value={adv.payment_mode || ''} onChange={(v) => setAdvField('payment_mode', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...PAYMENT_MODES]} />
                <FilterSelect label="Shipping Partner" value={adv.shipping_partner_id || ''} onChange={(v) => setAdvField('shipping_partner_id', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...shippingPartners.map((p) => ({ v: p.id, l: p.name }))]} />
                <FilterSelect label="Freight Mode" value={adv.freight_mode || ''} onChange={(v) => setAdvField('freight_mode', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...FREIGHT_MODES]} />
                <FilterSelect label="Pickup Type" value={adv.pickup_type || ''} onChange={(v) => setAdvField('pickup_type', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...PICKUP_TYPES]} />
                <FilterSelect label="Insurance" value={adv.insurance_option || ''} onChange={(v) => setAdvField('insurance_option', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...INSURANCE_OPTIONS]} />
                <FilterSelect label="LRN Assigned" value={adv.lrn_assigned || ''} onChange={(v) => setAdvField('lrn_assigned', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...YES_NO]} />
                <FilterSelect label="E-way Bill" value={adv.eway_bill || ''} onChange={(v) => setAdvField('eway_bill', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...EWAY_OPTS]} />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Quantities &amp; Amounts</p>
                <NumRangeField label="Boxes" lo={adv.boxes_min || ''} hi={adv.boxes_max || ''} onLo={(v) => setAdvField('boxes_min', v)} onHi={(v) => setAdvField('boxes_max', v)} />
                <NumRangeField label="Invoice Amount (₹)" lo={adv.amount_min || ''} hi={adv.amount_max || ''} onLo={(v) => setAdvField('amount_min', v)} onHi={(v) => setAdvField('amount_max', v)} />
              </div>
            </div>
            <SheetFooter className="flex-row gap-2">
              <Button variant="outline" onClick={handleClearFilters} className="flex-1">Clear all</Button>
              <Button onClick={() => setFilterOpen(false)} className="flex-1" style={{ backgroundColor: '#00A699' }}>Done</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Orders Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading...</div>
            ) : orders.length === 0 ? (
              <div className="p-8 text-center text-slate-600">No appointments found</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LRN</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Reference No.</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Marketplace</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Seller</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice No.</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Appointment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        {!isActiveTab && (
                          <>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">GRN Status</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Dispatch Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Accepted Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rejected Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Collected</th>
                          </>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle text-sm">
                            <span className="font-medium text-teal-600">{order.lrn || 'N/A'}</span>
                            <div>
                              <OrderFlagBadge
                                flags={flagsByOrder[order.id]}
                                onClick={() => { setFlagsOrder(order); setShowFlagsDialog(true); }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <button
                              onClick={() => navigate(`/admin/orders/${order.id}`)}
                              className="text-blue-600 hover:underline"
                            >
                              {order.InfoFromPartnerLRCopy?.shippers_reference_no || order.reference_number || '-'}
                            </button>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">{order.b2b_marketplace_name || 'N/A'}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="font-medium text-slate-800">{order.seller_brand_name || order.seller_id}</div>
                            <SellerBalance wallet={order.seller_wallet} />
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">{order.InfoFromPartnerLRCopy?.invoice_no || order.invoice_number}</td>
                          <td className="px-4 py-3 align-middle text-sm font-medium text-right">₹{(order.InfoFromPartnerLRCopy?.total_invoice_value ? parseFloat(order.InfoFromPartnerLRCopy.total_invoice_value) : (order.invoice_amount || 0)).toLocaleString()}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            {order.appointment_date ? (
                              <div className="whitespace-nowrap">
                                <div>{order.appointment_date}</div>
                                {order.appointment_time_range && (
                                  <div className="text-xs text-slate-500">{order.appointment_time_range}</div>
                                )}
                              </div>
                            ) : order.appointment_datetime ? (
                              <div className="whitespace-nowrap">
                                <div>{formatToIST(order.appointment_datetime, false)}</div>
                                <div className="text-xs text-slate-500">{formatToIST(order.appointment_datetime).split(', ')[1]}</div>
                              </div>
                            ) : (
                              'N/A'
                            )}
                            {isDelhiveryAppointmentBooked(order) && (
                              <div className="mt-1">
                                <Badge className="bg-green-100 text-green-800 text-xs" title={order.delhivery_appointment_id ? `Booked with Delhivery — Appointment ID: ${order.delhivery_appointment_id}` : "Booked with Delhivery"}>
                                  Booked ✓
                                </Badge>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="whitespace-nowrap">
                              <div>{formatToIST(order.created_at, false)}</div>
                              <div className="text-xs text-slate-500">{formatToIST(order.created_at).split(', ')[1]}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <Badge className={STATUS_COLORS[order.status] || "bg-gray-100 text-gray-800"}>
                              {order.status}
                            </Badge>
                          </td>
                          {!isActiveTab && (
                            <>
                              <td className="px-4 py-3 align-middle text-sm">
                                <Badge className={GRN_STATUS_COLORS[order.grn_status] || "bg-gray-100 text-gray-800"}>
                                  {order.grn_status}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{order.total_dispatch_quantity || 0}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{order.total_accepted_quantity || 0}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{order.total_rejected_quantity || 0}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">
                                <button
                                  onClick={() => handleViewPayments(order)}
                                  className="text-blue-600 hover:underline"
                                >
                                  ₹{order.amount_collected?.toLocaleString() || 0}
                                </button>
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setHistoryOrder(order); setShowHistoryDialog(true); }}
                                title="Appointment edit history"
                              >
                                <History className="w-4 h-4 mr-1" />
                                History
                              </Button>
                              {isActiveTab && order.shipping_partner_name?.toLowerCase().includes('delhivery') && order.lrn && order.status !== 'OFD' && (
                                <Button
                                  size="sm"
                                  style={{ backgroundColor: '#00A699' }}
                                  onClick={() => handleScheduleAppointment(order)}
                                  disabled={schedulingOrderId === order.id || order.seller_wallet?.is_low}
                                  title={order.seller_wallet?.is_low
                                    ? "Seller wallet is at/below the minimum — recharge required before scheduling"
                                    : (isDelhiveryAppointmentBooked(order) ? "Reschedule appointment with Delhivery" : "Schedule appointment with shipping partner")}
                                >
                                  {schedulingOrderId === order.id ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                      {isDelhiveryAppointmentBooked(order) ? 'Rescheduling...' : 'Scheduling...'}
                                    </>
                                  ) : (
                                    <>
                                      <Calendar className="w-4 h-4 mr-1" />
                                      {isDelhiveryAppointmentBooked(order) ? 'Reschedule' : 'Schedule'}
                                    </>
                                  )}
                                </Button>
                              )}
                              {/* Edit Appointment Button - Only for orders with existing appointments */}
                              {isActiveTab && order.appointment_date && order.shipping_partner_name?.toLowerCase().includes('delhivery') && order.lrn && order.status !== 'OFD' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenEditAppointment(order)}
                                  disabled={!canEditAppointment(order) || editingAppointmentId === order.id}
                                  title={!canEditAppointment(order) ? getEditDisabledReason(order) : "Edit appointment"}
                                  data-testid={`edit-appointment-btn-${order.id}`}
                                >
                                  {editingAppointmentId === order.id ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Edit className="w-4 h-4 mr-1" />
                                      Edit
                                    </>
                                  )}
                                </Button>
                              )}
                              {/* Manual Update — EdgeLMS only (no carrier call); partner-agnostic. */}
                              {isActiveTab && order.status !== 'OFD' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenManualUpdate(order)}
                                  disabled={editingAppointmentId === order.id}
                                  title="Update the appointment in EdgeLMS only (record a partner-confirmed appointment without calling the carrier)"
                                  data-testid={`manual-update-btn-${order.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Manual Update
                                </Button>
                              )}
                              {!isActiveTab && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenGRNDialog(order)}
                                    title="Update GRN"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenPaymentDialog(order)}
                                    title="Record Payment"
                                  >
                                    <Wallet className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    Showing {orders.length} of {totalOrders} appointments
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* GRN Dialog */}
      <Dialog open={showGRNDialog} onOpenChange={setShowGRNDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update GRN Status</DialogTitle>
            <DialogDescription>
              Update Goods Receipt Note for Order: {selectedOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>GRN Status</Label>
              <Select value={grnStatus} onValueChange={setGrnStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRN_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Total Dispatch Quantity</Label>
              <Input
                type="number"
                value={dispatchQty}
                onChange={(e) => setDispatchQty(e.target.value)}
                className="mt-1"
                min="0"
              />
            </div>
            
            <div>
              <Label>Total Accepted Quantity</Label>
              <Input
                type="number"
                value={acceptedQty}
                onChange={(e) => setAcceptedQty(e.target.value)}
                className="mt-1"
                min="0"
              />
            </div>
            
            <div>
              <Label>Total Rejected Quantity</Label>
              <Input
                type="number"
                value={rejectedQty}
                onChange={(e) => setRejectedQty(e.target.value)}
                className="mt-1"
                min="0"
              />
            </div>
            
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={grnRemarks}
                onChange={(e) => setGrnRemarks(e.target.value)}
                className="mt-1"
                placeholder="Add any notes or remarks..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGRNDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveGRN}>Save GRN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record payment collection for Order: {selectedOrder?.order_number}
              <br />
              <span className="font-semibold">Invoice Amount: ₹{selectedOrder?.invoice_amount?.toLocaleString()}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Amount Collected (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="mt-1"
                placeholder="Enter amount"
                min="0"
              />
            </div>
            
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Transaction Reference (Optional)</Label>
              <Input
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                className="mt-1"
                placeholder="Transaction ID, Cheque No., etc."
              />
            </div>
            
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={paymentRemarks}
                onChange={(e) => setPaymentRemarks(e.target.value)}
                className="mt-1"
                placeholder="Add any notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payments List Dialog */}
      <Dialog open={showPaymentsListDialog} onOpenChange={setShowPaymentsListDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payment History</DialogTitle>
            <DialogDescription>
              Order: {selectedOrder?.order_number} | Invoice: ₹{selectedOrder?.invoice_amount?.toLocaleString()}
              <br />
              <span className="text-green-600 font-semibold">Total Collected: ₹{paymentsTotal?.toLocaleString()}</span>
              {" | "}
              <span className={paymentsRemaining > 0 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                Remaining: ₹{paymentsRemaining?.toLocaleString()}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {paymentsList.length === 0 ? (
              <p className="text-center text-slate-600 py-8">No payments recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Method</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {paymentsList.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-4 py-3 align-middle text-sm">
                          <div className="whitespace-nowrap">
                            <div>{formatToIST(payment.payment_date, false)}</div>
                            <div className="text-xs text-slate-500">{formatToIST(payment.payment_date).split(', ')[1]}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle text-sm">{payment.payment_method}</td>
                        <td className="px-4 py-3 align-middle text-sm font-medium text-green-600 text-right">₹{payment.amount_collected?.toLocaleString()}</td>
                        <td className="px-4 py-3 align-middle text-sm">{payment.transaction_reference || '-'}</td>
                        <td className="px-4 py-3 align-middle text-sm">{payment.remarks || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPaymentsListDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Edit History */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Appointment Edit History</DialogTitle>
            <DialogDescription>
              {historyOrder ? `LRN ${historyOrder.lrn || '—'} · Order ${historyOrder.order_number || historyOrder.id}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 py-2">
            <div className="mb-4 rounded-md bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Current appointment</div>
              <div className="text-sm font-medium text-slate-800">
                {historyOrder?.appointment_date || (historyOrder?.appointment_datetime ? formatToIST(historyOrder.appointment_datetime, false) : '—')}
                {historyOrder?.appointment_time_range ? ` · ${historyOrder.appointment_time_range}` : ''}
              </div>
            </div>
            {getApptHistory(historyOrder).length === 0 ? (
              <p className="text-sm text-slate-500">No appointment edit history recorded for this order yet.</p>
            ) : (
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {getApptHistory(historyOrder).map((h, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-slate-400" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{APPT_ACTION_LABEL[h.status] || h.status}</span>
                      <span className="text-xs text-slate-400">{formatToIST(h.timestamp)}</span>
                    </div>
                    {h.remarks && <div className="text-sm text-slate-600 mt-0.5">{h.remarks}</div>}
                    <div className="text-xs text-slate-400 mt-0.5">
                      By {h.updated_by || 'system'}{h.user_role ? ` (${h.user_role})` : ''}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-LRN Ops Red Flags panel */}
      <OrderRedFlagsDialog
        open={showFlagsDialog}
        onOpenChange={setShowFlagsDialog}
        order={flagsOrder}
        flags={flagsOrder ? (flagsByOrder[flagsOrder.id] || []) : []}
        availableActions={availableActions}
        canAct={can(user, "red_flags:act")}
        onActioned={() => flagsOrder && refreshOrderFlags(flagsOrder.id)}
      />

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditAppointmentDialog} onOpenChange={setShowEditAppointmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{manualMode ? "Manual Update — EdgeLMS only" : "Edit Appointment"}</DialogTitle>
            <DialogDescription>
              Update appointment for Order: {editingAppointmentOrder?.order_number}
              <br />
              LRN: {editingAppointmentOrder?.lrn}
            </DialogDescription>
          </DialogHeader>
          {manualMode && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This updates the appointment in <strong>EdgeLMS only</strong> — the shipping partner is <strong>not</strong> called. Record how it was confirmed with the partner below.
            </div>
          )}
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Appointment</Label>
              <div className="mt-1 p-3 bg-slate-50 rounded text-sm">
                {editingAppointmentOrder?.appointment_date} ({editingAppointmentOrder?.appointment_time_range || 'N/A'})
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit_appt_date">New Appointment Date</Label>
              <Input
                id="edit_appt_date"
                type="date"
                value={editAppointmentDate}
                onChange={(e) => setEditAppointmentDate(e.target.value)}
                min={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Appointments can only be scheduled for day after tomorrow onwards
              </p>
            </div>
            
            <div>
              <Label>Time Slot</Label>
              <Select value={editAppointmentTime} onValueChange={setEditAppointmentTime}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              {(() => { const req = !!marketplaces.find((m) => m.id === editingAppointmentOrder?.b2b_marketplace_id)?.appointment_id_required; return (<>
              <Label>Delhivery Appointment ID {req ? <span className="text-red-500">*</span> : <span className="text-slate-400">(optional)</span>}</Label>
              <Input
                value={editApptDelhiveryId}
                onChange={(e) => setEditApptDelhiveryId(e.target.value)}
                className="mt-1"
                placeholder="Enter Delhivery appointment ID"
              />
              {req && <p className="text-xs text-red-500 mt-1">Required to book/reschedule for this marketplace.</p>}
              </>); })()}
            </div>

            {manualMode && (
              <>
                <div>
                  <Label>How was it confirmed with the partner? <span className="text-red-500">*</span></Label>
                  <Select value={manualMethod} onValueChange={setManualMethod}>
                    <SelectTrigger className="mt-1" data-testid="manual-method">
                      <SelectValue placeholder="Select confirmation method" />
                    </SelectTrigger>
                    <SelectContent>
                      {MANUAL_METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {manualMethod === "other" && (
                  <div>
                    <Label htmlFor="manual_method_other">Describe the method <span className="text-red-500">*</span></Label>
                    <Input
                      id="manual_method_other"
                      value={manualMethodOther}
                      onChange={(e) => setManualMethodOther(e.target.value)}
                      className="mt-1"
                      placeholder="e.g. Confirmed on a call with the partner ops team"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="manual_comment">Comment <span className="text-slate-400">(optional)</span></Label>
                  <Textarea
                    id="manual_comment"
                    value={manualComment}
                    onChange={(e) => setManualComment(e.target.value)}
                    className="mt-1"
                    rows={2}
                    placeholder="Any note for the appointment history"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditAppointmentDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={manualMode ? handleManualUpdate : handleEditAppointment}
              disabled={editingAppointmentId === editingAppointmentOrder?.id}
            >
              {editingAppointmentId === editingAppointmentOrder?.id ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  {manualMode ? "Updating…" : "Updating…"}
                </>
              ) : (
                manualMode ? "Update in EdgeLMS only" : "Add/Update Appointment in LMS and Shipping Partner"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Appointment Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Appointment with Delhivery</DialogTitle>
            <DialogDescription>
              Order: {scheduleOrder?.order_number}
              <br />
              LRN: {scheduleOrder?.lrn}
              <br />
              Appointment: {scheduleOrder?.appointment_date} ({scheduleOrder?.appointment_time_range || 'N/A'})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="po_numbers">
                PO Numbers <span className="text-red-500">*</span>
              </Label>
              <Input
                id="po_numbers"
                value={poNumbersInput}
                onChange={(e) => {
                  setPoNumbersInput(e.target.value);
                  setPoNumbersError("");
                }}
                className={`mt-1 ${poNumbersError ? 'border-red-500' : ''}`}
                placeholder="Enter PO numbers (comma-separated)"
                data-testid="po-numbers-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Max 5 PO numbers, separated by comma. Enter &quot;NotApplicable&quot; if PO is not applicable.
              </p>
              {poNumbersError && (
                <p className="text-xs text-red-500 mt-1">{poNumbersError}</p>
              )}
            </div>

            <div>
              <Label htmlFor="po_expiry_date">
                PO Expiry Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="po_expiry_date"
                type="date"
                value={poExpiryInput}
                onChange={(e) => setPoExpiryInput(e.target.value)}
                className="mt-1"
                data-testid="po-expiry-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {scheduleOrder?.po_expiry_date
                  ? "Required by Delhivery. Pre-filled from the order — edit if needed."
                  : "Required by Delhivery. Not set on this order — please enter it to book. This will be saved on the order."}
              </p>
            </div>

            <div>
              {(() => { const req = !!marketplaces.find((m) => m.id === scheduleOrder?.b2b_marketplace_id)?.appointment_id_required; return (<>
              <Label htmlFor="appointment_id">
                Delhivery Appointment ID {req ? <span className="text-red-500">*</span> : <span className="text-slate-400">(optional)</span>}
              </Label>
              <Input
                id="appointment_id"
                value={appointmentIdInput}
                onChange={(e) => setAppointmentIdInput(e.target.value)}
                className="mt-1"
                placeholder="Enter Delhivery appointment ID"
                data-testid="appointment-id-input"
              />
              {req && <p className="text-xs text-red-500 mt-1">
                Required to book/reschedule the appointment for this marketplace.
              </p>}
              </>); })()}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowScheduleDialog(false);
                setScheduleOrder(null);
                setPoNumbersInput("");
                setAppointmentIdInput("");
                setPoNumbersError("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSchedule}
              style={{ backgroundColor: '#00A699' }}
              data-testid="confirm-schedule-btn"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Add/Update Appointment in LMS and Shipping Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAppointments;
